// Resource module — the platform announcements board (/announcements public
// read + /admin/announcements operator CRUD). React-free: pure fetchers +
// key builders.
//
// GET    /announcements                        → {items: PublicAnnouncement[]}
// GET    /admin/announcements                  → {items: Announcement[]}
// POST   /admin/announcements                  → {entry: Announcement}
// PATCH  /admin/announcements/{announcementId} → {entry: Announcement}
// DELETE /admin/announcements/{announcementId} → {deleted: true}
//
// PLATFORM-LEVEL, not org-scoped: no ?orgId= rides any call — the board is
// identical for every reader, and the public list answers unauthenticated
// landing visitors. Keys are therefore plain `as const` tuples, not
// OrgQueryKey factories — never hang this domain on useOrgQuery.
import { apiFetch } from '@/lib/api'
import { toConsolePath, toPublicPath } from '@/lib/consolePath'
import type { components } from '@/types/openapi'

/** The reader-facing row GET /announcements serves (landing + console). */
export type PublicAnnouncement = components['schemas']['PublicAnnouncement']
/** The operator-facing full row /admin/announcements serves. */
export type AdminAnnouncement = components['schemas']['Announcement']
export type AnnouncementKind = PublicAnnouncement['kind']

/** Write payload for POST /admin/announcements (operator console form). */
export interface CreateAnnouncementInput {
  kind?: AnnouncementKind
  pinned?: boolean
  titleZh: string
  bodyZh?: string
  titleEn?: string | null
  bodyEn?: string | null
  actionUrl?: string | null
  startsAt?: string | null
  endsAt?: string | null
}

/** PATCH /admin/announcements/{id}: absent field untouched, explicit null clears. */
export type UpdateAnnouncementInput = Partial<CreateAnnouncementInput>

// Plain non-org tuples — a platform-level resource carries no org segment.
export const announcementsKeys = {
  /** The live public board (landing bar + dashboard section + banner share it). */
  public: ['announcements', 'public'] as const,
  /** The operator board — every row incl. scheduled/expired. */
  admin: ['announcements', 'admin'] as const,
}

export async function fetchPublicAnnouncements(): Promise<PublicAnnouncement[]> {
  return (await apiFetch<{ items: PublicAnnouncement[] }>('/announcements')).items
}

export async function fetchAdminAnnouncements(): Promise<AdminAnnouncement[]> {
  return (await apiFetch<{ items: AdminAnnouncement[] }>('/admin/announcements')).items
}

export async function createAnnouncement(input: CreateAnnouncementInput): Promise<AdminAnnouncement> {
  return (await apiFetch<{ entry: AdminAnnouncement }>('/admin/announcements', {
    method: 'POST',
    body: JSON.stringify(input),
  })).entry
}

export async function updateAnnouncement(
  announcementId: string,
  input: UpdateAnnouncementInput,
): Promise<AdminAnnouncement> {
  return (await apiFetch<{ entry: AdminAnnouncement }>(
    `/admin/announcements/${encodeURIComponent(announcementId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  )).entry
}

export async function deleteAnnouncement(announcementId: string): Promise<void> {
  await apiFetch<{ deleted: true }>(`/admin/announcements/${encodeURIComponent(announcementId)}`, { method: 'DELETE' })
}

/**
 * The announcement's CTA target — a console-relative path or null. The same
 * whitelist the notifications inbox applies (toConsolePath): the backend
 * already refuses absolute/protocol-relative/backslash shapes at write
 * time, this is the render-side belt so a legacy or hand-edited row still
 * degrades to copy instead of becoming a link off the product.
 */
export function announcementActionPath(item: Pick<PublicAnnouncement, 'actionUrl'>): string | null {
  return toConsolePath(item.actionUrl)
}

/**
 * 落地页公告栏的 CTA 目标 — 语法闸门之外再过一道公开面白名单
 * （`toPublicPath`）。控制台侧（收件箱、公告管理板）仍用 `announcementActionPath`
 * 拿深链；只有落地页在登录之前渲染，那里指向 `/dashboard` 的条目对匿名读者是死链。
 */
export function announcementPublicActionPath(item: Pick<PublicAnnouncement, 'actionUrl'>): string | null {
  return toPublicPath(item.actionUrl)
}

/** Localized copy with zh fallback — titleZh is required at write time, the
 *  en fields are optional, so a zh-only announcement is a normal state. */
export function announcementTitle(item: PublicAnnouncement, locale: string): string {
  return (locale.startsWith('en') ? item.titleEn : null) ?? item.titleZh
}

export function announcementBody(item: PublicAnnouncement, locale: string): string {
  return (locale.startsWith('en') ? item.bodyEn : null) ?? item.bodyZh
}
